import React, { useState } from 'react';
import {
  Stack,
  Typography,
  Paper,
  Box,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Collapse,
  Button,
  Alert,
  AlertTitle,
} from '@mui/material';
import {
  ContentCopy,
  ExpandMore,
  ExpandLess,
  Code,
  Api,
  Description,
  Security,
} from '@mui/icons-material';
import { Frame } from '../../components/layouts/Frame';

interface CodeExample {
  code: string;
  response: string;
}

interface CodeBlockProps {
  code: string;
  language: string;
}

interface ResponseBlockProps {
  response: string;
}

interface APISectionProps {
  title: string;
  description: string;
  endpoint: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  examples: CodeExample[];
}

const CodeBlock: React.FC<CodeBlockProps> = ({ code, language }) => {
  const [copied, setCopied] = useState<boolean>(false);

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  return (
    <Box sx={{ position: 'relative', mb: 2 }}>
      <Paper
        elevation={1}
        sx={{
          p: 2,
          bgcolor: '#f5f5f5',
          fontFamily: 'monospace',
          fontSize: '0.875rem',
          overflow: 'auto',
          maxHeight: '400px',
        }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}>
          <Chip label={language} size="small" color="primary" />
          <IconButton
            size="small"
            onClick={handleCopy}
            sx={{ color: copied ? 'success.main' : 'text.secondary' }}>
            <ContentCopy fontSize="small" />
          </IconButton>
        </Box>
        <pre
          style={{
            margin: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}>
          {code}
        </pre>
      </Paper>
    </Box>
  );
};

const ResponseBlock: React.FC<ResponseBlockProps> = ({ response }) => {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, color: 'success.main' }}>
        Response:
      </Typography>
      <Paper
        elevation={1}
        sx={{
          p: 2,
          bgcolor: '#f0f8f0',
          fontFamily: 'monospace',
          fontSize: '0.875rem',
          border: '1px solid #e0e0e0',
        }}>
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{response}</pre>
      </Paper>
    </Box>
  );
};

const APISection: React.FC<APISectionProps> = ({
  title,
  description,
  endpoint,
  method,
  examples,
}) => {
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [expanded, setExpanded] = useState<boolean>(true);

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: number,
  ): void => {
    setSelectedTab(newValue);
  };

  const languages: string[] = ['PHP', 'Node.js', 'React', 'Python'];

  const getMethodColor = (
    method: string,
  ): 'error' | 'success' | 'warning' | 'primary' => {
    switch (method) {
      case 'POST':
        return 'error';
      case 'GET':
        return 'success';
      case 'PUT':
        return 'warning';
      case 'DELETE':
        return 'error';
      default:
        return 'primary';
    }
  };

  return (
    <Card sx={{ mb: 4 }}>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Api color="primary" />
            <Typography variant="h5" component="h2">
              {title}
            </Typography>
            <Chip label={method} color={getMethodColor(method)} size="small" />
          </Box>
          <IconButton onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Endpoint:
          </Typography>
          <Paper
            elevation={0}
            sx={{
              px: 2,
              py: 0.5,
              bgcolor: '#f5f5f5',
              fontFamily: 'monospace',
              fontSize: '0.875rem',
            }}>
            {endpoint}
          </Paper>
        </Box>

        <Collapse in={expanded}>
          <Divider sx={{ my: 2 }} />

          <Box sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={selectedTab} onChange={handleTabChange}>
                {languages.map((lang, index) => (
                  <Tab key={lang} label={lang} />
                ))}
              </Tabs>
            </Box>

            <Box sx={{ mt: 2 }}>
              {examples.map((example, index) => (
                <Box
                  key={index}
                  sx={{ display: selectedTab === index ? 'block' : 'none' }}>
                  <CodeBlock code={example.code} language={languages[index]} />
                  <ResponseBlock response={example.response} />
                </Box>
              ))}
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export const Documentation: React.FC = () => {
  const extractExamples: CodeExample[] = [
    {
      code: `<?php

  $curl = curl_init();
        
  curl_setopt_array($curl, array(
    CURLOPT_URL => 'https://api.aiassetregister.com/api/extract',
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_ENCODING => '',
    CURLOPT_MAXREDIRS => 10,
    CURLOPT_TIMEOUT => 0,
    CURLOPT_FOLLOWLOCATION => true,
    CURLOPT_HTTP_VERSION => CURL_HTTP_VERSION_1_1,
    CURLOPT_CUSTOMREQUEST => 'POST',
    CURLOPT_POSTFIELDS => array(
      'image' => new CURLFile('test.jpg'),
      'query[]' => ['serial number', 'manufacturer'],
      'detail' => 3
    ),
    CURLOPT_HTTPHEADER => array(
      'X-API-Key: {X-APIKEY}',
      'X-API-Secret: {X-APISECRET}',
    ),
  ));

  $response = curl_exec($curl);

  curl_close($curl);
  echo $response;

?>`,
      response: `{ "Credits Remaining": 28, "Credits Used": 72, "Job Ref": "ASJID_f4gimPZkdcKIzJ82", "Query Made": 24, "manufacturer": "CISCO", "serial_number": "CCQ165218CB"}`,
    },
    {
      code: `var request = require('request');
var fs = require('fs');

const options = {
  method: 'POST',
  url: 'https://api.aiassetregister.com/api/extract',
  headers: {
    'x-API-Key': '{X-APIKEY}',
    'X-API-Secret': '{X-APISECRET}',
  },
  formData: {
    image: fs.createReadStream('test.jpg'),
    'query[]': ['serial number', 'manufacturer'],
    detail: 3,
  },
};

request(options, function (error, response) {
  if (error) throw new Error(error);
  console.log(response.body);
});`,
      response: `{"serial_number":"MMT1MEE00465200A6E4208","manufacturer":"acer","Job Ref":"ASJID_N1J8fkhBtc1W0Rrh","Credits Used":81,"Credits Remaining":491,"Query Made":27}`,
    },
    {
      code: `var axios = require('axios');
var fs = require('fs');
const FormData = require('form-data');

const form = new FormData();
form.append('image', fs.createReadStream('test.jpg'));
form.append('query[]', 'serial number');
form.append('query[]', 'manufacturer');
form.append('detail', 3);

var config = {
  method: 'POST',
  url: 'https://api.aiassetregister.com/api/extract',
  headers: {
    'x-API-Key': '{X-APIKEY}',
    'X-API-Secret': '{X-APISECRET}',
    ...form.getHeaders(),
  },
  data: form,
};

axios(config)
  .then(function (response) {
    console.log(JSON.stringify(response.data));
  })
  .catch(function (error) {
    console.log(error);
  });`,
      response: `{"serial_number":"MMT1MEE00465200A6E4208","manufacturer":"acer","Job Ref":"ASJID_N1J8fkhBtc1W0Rrh","Credits Used":81,"Credits Remaining":491,"Query Made":27}`,
    },
    {
      code: `import requests
      
url = "https://api.aiassetregister.com/api/extract"

headers = {
  'x-API-Key': '{X-APIKEY}',
  'X-API-Secret': '{X-APISECRET}'
}

files = {
  'image': open('test.jpg', 'rb')
}

data = {
  'query[]': ['serial number', 'manufacturer'],
  'detail': 3
}

response = requests.post(url, headers=headers, files=files, data=data)

print(response.text)`,
      response: `{"serial_number":"MMT1MEE00465200A6E4208","manufacturer":"Acer","Job Ref":"ASJID_FH0uz7oEricYCGw0","Credits Used":84,"Credits Remaining":488,"Query Made":28}`,
    },
  ];

  const correctionExamples: CodeExample[] = [
    {
      code: `<?php

  $curl = curl_init();
        
  curl_setopt_array($curl, array(
    CURLOPT_URL => 'https://api.aiassetregister.com/api/correction',
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_ENCODING => '',
    CURLOPT_MAXREDIRS => 10,
    CURLOPT_TIMEOUT => 0,
    CURLOPT_FOLLOWLOCATION => true,
    CURLOPT_HTTP_VERSION => CURL_HTTP_VERSION_1_1,
    CURLOPT_CUSTOMREQUEST => 'POST',
    CURLOPT_POSTFIELDS => array(
      'id' => 1,
      'information[Manufacturer]' => 'Dell-1',
      'information[Model Number]' => 'Latitude 55501',
      'information[Serial Number]' => '1234567891',
      'job_id' => 'ASJID_smTsGSEI9AdDERFw'
    ),
    CURLOPT_HTTPHEADER => array(
      'Accept: application/json',
      'X-API-Key: {X-APIKEY}',
      'X-API-Secret: {X-APISECRET}',
    ),
  ));

  $response = curl_exec($curl);

  curl_close($curl);
  echo $response;

?>`,
      response: `{"id":1,"user_id":3,"job_id":"ASJID_smTsGSEI9AdDERFw","information":"{\\"Manufacturer\\":\\"Dell-1\\",\\"Model Number\\":\\"Latitude 55501\\",\\"Serial Number\\":\\"1234567891\\"}","created_at":"2025-04-08T10:26:21.000000Z","updated_at":"2025-04-23T12:01:55.000000Z"}`,
    },
    {
      code: `var request = require('request');

const options = {
  method: 'POST',
  url: 'https://api.aiassetregister.com/api/correction',
  headers: {
    'Content-Type': 'application/json',
    'x-API-Key': '{X-APIKEY}',
    'X-API-Secret': '{X-APISECRET}',
  },
  json: {
    id: 1,
    job_id: 'ASJID_smTsGSEI9AdDERFw',
    information: {
      Manufacturer: 'Dell-1',
      'Model Number': 'Latitude 55501',
      'Serial Number': '1234567891',
    },
  },
};

request(options, function (error, response) {
  if (error) throw new Error(error);
  console.log(response.body);
});`,
      response: `{
  "id": 1,
  "user_id": 3,
  "job_id": "ASJID_smTsGSEI9AdDERFw",
  "information": "{\\"Manufacturer\\":\\"Dell-1\\",\\"Model Number\\":\\"Latitude 55501\\",\\"Serial Number\\":\\"1234567891\\"}",
  "created_at": "2025-04-08T10:26:21.000000Z",
  "updated_at": "2025-04-23T12:08:57.000000Z"
}`,
    },
    {
      code: `var axios = require('axios');

var config = {
  method: 'POST',
  url: 'https://api.aiassetregister.com/api/correction',
  headers: {
    'Content-Type': 'application/json',
    'x-API-Key': '{X-APIKEY}',
    'X-API-Secret': '{X-APISECRET}',
  },
  data: JSON.stringify({
    id: 1,
    job_id: 'ASJID_smTsGSEI9AdDERFw',
    information: {
      Manufacturer: 'Dell-1',
      'Model Number': 'Latitude 55501',
      'Serial Number': '1234567891',
    },
  }),
};

axios(config)
  .then(function (response) {
    console.log(JSON.stringify(response.data));
  })
  .catch(function (error) {
    console.log(error);
  });`,
      response: `{"id":1,"user_id":3,"job_id":"ASJID_smTsGSEI9AdDERFw","information":"{\\"Manufacturer\\":\\"Dell-1\\",\\"Model Number\\":\\"Latitude 55501\\",\\"Serial Number\\":\\"1234567891\\"}","created_at":"2025-04-08T10:26:21.000000Z","updated_at":"2025-04-23T12:10:45.000000Z"}`,
    },
    {
      code: `import requests

url = "https://api.aiassetregister.com/api/correction"

headers = {
  'Content-Type': 'application/json',
  'x-API-Key': '{X-APIKEY}',
  'X-API-Secret': '{X-APISECRET}',
}

payload = {
  "id": 1,
  "job_id": "ASJID_smTsGSEI9AdDERFw",
  "information": {
      "Manufacturer": "Dell-1",
      "Model Number": "Latitude 55501",
      "Serial Number": "1234567891"
  }
}

response = requests.post(url, headers=headers, json=payload)

print(response.text)`,
      response: `{"id":1,"user_id":3,"job_id":"ASJID_smTsGSEI9AdDERFw","information":"{\\"Manufacturer\\":\\"Dell-1\\",\\"Model Number\\":\\"Latitude 55501\\",\\"Serial Number\\":\\"1234567891\\"}","created_at":"2025-04-08T10:26:21.000000Z","updated_at":"2025-04-23T12:12:21.000000Z"}`,
    },
  ];

  return (
    <Frame title="Documentation">
      <Stack spacing={4}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom>
            API Documentation
          </Typography>
          <Typography variant="h6" color="text.secondary">
            AI Asset Register API - Extract and Correction Services
          </Typography>
        </Box>

        <APISection
          title="Extract API"
          description="Upload an image and extract specific information like serial numbers, manufacturers, and other asset details using AI-powered image recognition."
          endpoint="https://api.aiassetregister.com/api/extract"
          method="POST"
          examples={extractExamples}
        />

        <APISection
          title="Correction API"
          description="Submit corrections to previously extracted asset information to improve accuracy and maintain data quality."
          endpoint="https://api.aiassetregister.com/api/correction"
          method="POST"
          examples={correctionExamples}
        />

        <Alert severity="info" sx={{ mt: 4 }}>
          <AlertTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Security fontSize="small" />
              Authentication Required
            </Box>
          </AlertTitle>
          <Typography variant="body2" sx={{ mb: 2 }}>
            All API requests require authentication using your API key and
            secret. Include these in your request headers:
          </Typography>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: '#f5f5f5',
              fontFamily: 'monospace',
              fontSize: '0.875rem',
            }}>
            X-API-Key: {'{X-APIKEY}'}
            <br />
            X-API-Secret: {'{X-APISECRET}'}
          </Paper>
        </Alert>

        <Paper elevation={1} sx={{ p: 3, bgcolor: '#f8f9fa', mt: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Description color="primary" />
            <Typography variant="h6">Getting Started</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            To use the AI Asset Register API, you'll need to:
          </Typography>
          <Stack spacing={1} sx={{ ml: 2 }}>
            <Typography variant="body2">
              • Obtain your API credentials (X-APIKEY and X-APISECRET)
            </Typography>
            <Typography variant="body2">
              • Choose the appropriate endpoint for your use case
            </Typography>
            <Typography variant="body2">
              • Follow the code examples for your preferred programming language
            </Typography>
            <Typography variant="body2">
              • Handle responses and errors appropriately
            </Typography>
          </Stack>
        </Paper>
      </Stack>
    </Frame>
  );
};
